import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button";
import { IconButton } from "./IconButton";

type ModalProps = {
  title: string;
  content?: string;
  children?: ReactNode;
  open?: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  state?: "default" | "loading" | "error";
  errorMessage?: string;
  showDefaultButtons?: boolean;
  confirmLabel?: string;
  onConfirm?: () => void;
  showCloseButton?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
};

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  "2xl": "max-w-4xl",
  "3xl": "max-w-5xl",
  "4xl": "max-w-6xl",
};

export function Modal({
  title,
  content,
  children,
  open = true,
  onClose,
  onOpenChange,
  state = "default",
  errorMessage,
  showDefaultButtons = true,
  confirmLabel = "Confirmer",
  onConfirm,
  showCloseButton = true,
  size = "md",
}: ModalProps) {
  const handleClose = () => {
    if (onOpenChange) {
      onOpenChange(false);
    } else if (onClose) {
      onClose();
    }
  };
  
  if (!open) return null;
  
  return createPortal(
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => {
        // Ne pas fermer au clic sur le backdrop pour éviter les fermetures accidentelles
        // if (e.target === e.currentTarget) {
        //   handleClose();
        // }
      }}
    >
      <div 
        className={`w-full ${sizeClasses[size]} max-h-[90vh] flex flex-col rounded-lg border border-border bg-surface p-6 shadow-xl mx-4`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 mb-4">
          <h2 className="text-lg font-semibold text-text">{title}</h2>
          {showCloseButton && (
            <IconButton
              icon="✕"
              ariaLabel="Fermer"
              intent="ghost"
              size="sm"
              onClick={handleClose}
            />
          )}
        </div>
        {content ? <div className="mb-4 text-sm text-muted-foreground">{content}</div> : null}
        {children ? <div className="flex-1 min-h-0 overflow-y-auto">{children}</div> : null}
        {state === "error" && errorMessage ? (
          <div className="mt-4 rounded-md border border-danger bg-danger/10 p-3">
            <div className="text-sm text-danger">{errorMessage}</div>
          </div>
        ) : null}
        {showDefaultButtons && (
          <div className="mt-6 flex justify-end gap-2 border-t border-border pt-4">
            <Button 
              label="Fermer" 
              intent="default" 
              size="sm" 
              onClick={handleClose} 
              state={state === "loading" ? "disabled" : "default"} 
            />
            <Button
              label={confirmLabel}
              intent="primary"
              size="sm"
              onClick={onConfirm || handleClose}
              state={state === "loading" ? "loading" : "default"}
            />
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
